/**
 * Writes content to the user's clipboard. When `html` is provided, writes both
 * `text/plain` and `text/html` representations so rich-text consumers (e.g.
 * Google Docs, Gmail) paste formatted content while plain-text targets fall
 * back to `text`. Silently warns on failure or missing API.
 *
 * @param text - The plain-text representation to copy.
 * @param html - Optional HTML representation for rich-text paste targets.
 */
export const copyToClipboard = async (text: string, html?: string): Promise<void> => {
  if (!navigator.clipboard) {
    console.warn('Clipboard API not available');
    return;
  }

  // If HTML is provided, try to write both `text/plain` and `text/html` representations
  if (html && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);

      return;
    } catch (error) {
      // Fall back to plain-text copy so the user still gets something on their clipboard
      console.warn('Failed to copy rich text to clipboard, falling back to plain text', error);
    }
  }

  // If HTML is not provided, write only the plain-text representation
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.warn('Failed to copy to clipboard', error);
  }
};
